import {
  CAlert,
  CButton,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CLoadingButton,
  CSpinner,
} from '@coreui/react-pro'
import React, { useEffect, useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import ArticleService from 'src/service/ArticleService'
import ToastComponent from 'src/components/common/TaostComponent'
import ImageDropzone from 'src/components/common/ImageDropzone'
import useSponsorQuillModules from '../Sponsor/useSponsorQuillModules'

const API_URL = process.env.REACT_APP_API_URL
const SUPPORTED_FORMATS = ['image/jpg', 'image/png', 'image/jpeg', 'image/gif']
const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/gif'
const MAX_IMAGE_SIZE = 5.5 * 1024 * 1024 // 5.5MB

const validationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required').max(50, '50 Character Limit is allowed.'),
  image: Yup.mixed()
    .nullable()
    .test('size', 'File size too large, max file size is 5.5MB', (value) =>
      value ? value.size <= MAX_IMAGE_SIZE : true,
    )
    .test(
      'type',
      'Invalid file format selection',
      (value) => !value || SUPPORTED_FORMATS.includes(value?.type),
    ),
})

const EditForm = ({ articleId, visible, onClose, fetchUsers }) => {
  const [articleDetail, setArticleDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [loader, setLoader] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])
  const quillRef = useRef()
  const modules = useSponsorQuillModules(quillRef)

  useEffect(() => {
    if (visible && articleId) {
      setDetailLoading(true)
      ArticleService.getArticleDetail(articleId)
        .then((res) => {
          if (res.status === 200) {
            setArticleDetail(res.data)
          }
        })
        .catch((e) => {
          ToastComponent(e.response?.data?.message || 'Something went wrong.', 'error')
        })
        .finally(() => setDetailLoading(false))
    }
  }, [visible, articleId])

  const formik = useFormik({
    initialValues: {
      title: articleDetail?.title || '',
      external_link: articleDetail?.link || '',
      description: articleDetail?.description || '',
      link_type: articleDetail?.type || '',
      image: null,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data) => {
      const formData = new FormData()
      formData.append('articleId', articleId)
      formData.append('title', data.title)
      formData.append('description', data.description)
      formData.append('external_link', data.external_link)
      formData.append('type', data.link_type)
      if (data.image) formData.append('image', data.image)

      setLoader(true)
      ArticleService.editArticle(formData)
        .then((res) => {
          if (res.status === 200) {
            setErrorMessages([])
            fetchUsers?.()
            ToastComponent(res.message, 'success')
            onClose()
          } else {
            const errors =
              res.errors && Array.isArray(res.errors)
                ? res.errors
                : [res.message || 'Something went wrong.']
            setErrorMessages(errors)
          }
        })
        .catch(() => {
          ToastComponent('Something went wrong. Please try again.', 'error')
        })
        .finally(() => setLoader(false))
    },
  })

  const handleClose = () => {
    setErrorMessages([])
    setArticleDetail(null)
    formik.resetForm()
    onClose()
  }

  return (
    <COffcanvas
      placement="end"
      visible={visible}
      onHide={handleClose}
      backdrop
      style={{ width: 560 }}
    >
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Edit Article</COffcanvasTitle>
          <div className="text-medium-emphasis small">
            Update {articleDetail?.title || 'this article'}&apos;s details, image and description.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={handleClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        {errorMessages.length > 0 && (
          <div className="error-list">
            {errorMessages.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        {detailLoading ? (
          <div className="d-flex align-items-center gap-2 text-medium-emphasis py-4">
            <CSpinner size="sm" />
            <span>Loading article details...</span>
          </div>
        ) : (
          <CForm className="row g-3 flex-grow-1 align-content-start" onSubmit={formik.handleSubmit}>
            <CCol xs={12}>
              <CFormLabel htmlFor="title">Title *</CFormLabel>
              <CFormInput
                id="title"
                name="title"
                placeholder="Title"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.errors.title && formik.touched.title)}
              />
              {formik.errors.title && formik.touched.title && (
                <CFormFeedback invalid>{formik.errors.title}</CFormFeedback>
              )}
            </CCol>
            <CCol xs={12}>
              <CFormLabel htmlFor="image">Image</CFormLabel>
              <ImageDropzone
                accept={ACCEPTED_IMAGE_TYPES}
                hint="JPG, PNG or GIF. 350px x 220px recommended."
                minHeight={140}
                file={formik.values.image}
                existingUrl={
                  articleDetail?.image ? `${API_URL}uploads/articles/${articleDetail.image}` : null
                }
                error={formik.touched.image ? formik.errors.image : null}
                onChange={(selected) => {
                  formik.setTouched({ ...formik.touched, image: true })
                  formik.setFieldValue('image', selected)
                }}
              />
            </CCol>

            <CCol xs={12}>
              <hr className="mt-1 mb-0" />
              <h6 className="fw-bold mb-0 mt-3">Optional Link</h6>
            </CCol>
            <CCol xs={6}>
              <CFormLabel htmlFor="external_link">External Link</CFormLabel>
              <CFormInput
                id="external_link"
                name="external_link"
                placeholder="External Link"
                value={formik.values.external_link}
                onChange={formik.handleChange}
                aria-label="external_link"
              />
            </CCol>
            <CCol xs={6}>
              <CFormLabel htmlFor="link_type">Open Link</CFormLabel>
              <CFormSelect
                id="link_type"
                name="link_type"
                aria-label="Open Link"
                value={formik.values.link_type}
                onChange={formik.handleChange}
              >
                <option value="">Please select type</option>
                <option value={1}>Same Window</option>
                <option value={2}>New Tab</option>
              </CFormSelect>
            </CCol>

            <CCol xs={12}>
              <CFormLabel htmlFor="description">Short Description</CFormLabel>
              <div className="at-editor">
                <ReactQuill
                  theme="snow"
                  ref={quillRef}
                  value={formik.values.description}
                  onChange={(content) => formik.setFieldValue('description', content)}
                  modules={modules}
                />
              </div>
            </CCol>
          </CForm>
        )}
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={handleClose}>
          Cancel
        </CButton>
        <CLoadingButton color="primary" loading={loader} onClick={() => formik.handleSubmit()}>
          Save Changes
        </CLoadingButton>
      </div>
    </COffcanvas>
  )
}

export default EditForm
